import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Label from 'components/Luxkit/Label/Label'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import React, { ComponentProps, useCallback } from 'react'
import LuxPlusLogo from './Logos/LuxPlusLogo'
import LuxPlusBookingProtectionLabelLabel from './LuxPlusBookingProtectionLabel'
import LuxPlusInsuranceLabel from './LuxPlusInsuranceLabel'

const LOGO_HEIGHT = 10

interface Props extends Pick<ComponentProps<typeof Clickable>, 'to'>, Utils.PropsWithDataAttributes {
  onClick?: () => void;
  type: App.LuxPlusLabelType;
  saveAmount?: number;
  hideSaveAmountPlus?: boolean;
  currency?: string;
  description?: string;
}

function LuxPlusLabel(props: Props) {
  const {
    currency,
    description,
    type,
    onClick,
    saveAmount,
    hideSaveAmountPlus,
    to,
    ...rest
  } = props

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const clickHandler = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return
    e.preventDefault()
    e.stopPropagation()
    onClick()
  }, [onClick])

  if (!luxPlusEnabled) return null

  const saveAmountSuffix = hideSaveAmountPlus ? '' : '+'

  const label = <Label
    data-testid="lux-plus-label"
    {...rest}
    kind="primary"
    variant="lux-plus"
    endIcon={onClick ? <LineInfoCircleIcon /> : undefined}
  >
    {type === 'bonus-inclusions' && !description && <>Bonus inclusions with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
    {type === 'bonus-inclusions' && !!description && <>{description} with  <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
    {type === 'member' && <><LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> member</>}
    {type === 'only-logo' && <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />}
    {type === 'early-access-offer' && <>Early access with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT}/></>}
    {type === 'member-only-offer' && <><LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> member-only offer</>}
    {type === 'member-price-offer' && !!saveAmount && <>Save <FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix} with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
    {type === 'member-price-offer-lpp' && !!saveAmount && <>Save <FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix} with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
    {type === 'member-price-offer-tour' && !!saveAmount && <>Save <FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}/person with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
    {type === 'options-available-tour' && <><LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> options available</>}
    {type === 'plus' && <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} />}
    {type === 'plus-preview' && <><LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} /> preview</>}
    {type === 'insurance' && !!saveAmount && <LuxPlusInsuranceLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
    {type === 'booking-protection' && !!saveAmount && <LuxPlusBookingProtectionLabelLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
    {type === 'savings' && !!saveAmount && <>Extra <FormatCurrency format="roundedDollar" value={saveAmount} /> saved with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
  </Label>

  if (onClick || to) return <Clickable onClick={clickHandler} to={to} data-testid="lux-plus-label-clickable">{label}</Clickable>

  return label
}

export default LuxPlusLabel
